/**
 * In-process [P2pSession] pair, for connecting two nodes living in the same process — no real transport.
 *
 * The Web client uses this to wire a *summoned agent* node to the human's node (Milestone A1): the agent is a real,
 * separate peer whose commitments replicate through the very same [ReplicatedEventSync] delta-chain protocol a WebRTC
 * peer would use — only the transport is a direct method call. That keeps the human–agent boundary honest (the agent
 * cannot see or do anything a remote peer could not) instead of reaching into the human's node directly. It is also a
 * zero-latency stand-in for a live channel in tests.
 *
 * Delivery is synchronous: [sendRaw] hands the line straight to the peer's [Listener]. Lines pushed before the peer has
 * attached its listener are buffered and flushed once it does — mirroring a DataChannel's pre-open buffering — so the
 * order in which the two ends are handed to [ReplicatedEventSync.onSessionOpened] does not matter.
 *
 * Not thread-safe: confine a pair to one thread (both ends always are, by construction).
 */
package com.oasisfeng.invaluable.p2p.core

class LoopbackSession private constructor(override val outbound: Boolean) : P2pSession {

    override var isOpen: Boolean = true
        private set

    private var peer: LoopbackSession? = null
    private val pending = ArrayDeque<String>()

    override var listener: P2pSession.Listener? = null
        set(value) {
            field = value
            if (value != null) peer?.flush()   // the peer can now deliver whatever it buffered for us
        }

    override fun sendRaw(line: String) {
        if (!isOpen) return
        pending.addLast(line)
        flush()
    }

    /** Deliver everything queued here to the peer, if the peer has a listener attached; otherwise leave it buffered. */
    private fun flush() {
        val target = peer?.listener ?: return
        while (pending.isNotEmpty()) target.onLine(pending.removeFirst())
    }

    override fun close() {
        if (!isOpen) return
        isOpen = false
        listener?.onClosed()
        peer?.close()
    }

    companion object {
        /** A linked pair; hand one end to each node's [ReplicatedEventSync.onSessionOpened]. */
        fun pair(): Pair<LoopbackSession, LoopbackSession> {
            val a = LoopbackSession(outbound = true)
            val b = LoopbackSession(outbound = false)
            a.peer = b
            b.peer = a
            return a to b
        }
    }
}
